import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Save,
  Clock,
  CheckCircle,
  MapPin,
  Settings,
  Bell,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface ProfileFormData {
  business_name: string;
  business_type: string;
  phone: string;
  address: string;
  city: string;
  postcode: string;
  business_description: string;
  service_areas: string;
  latitude?: number;
  longitude?: number;
  opening_time: string;
  closing_time: string;
  availability: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  capabilities: {
    planning_flooring: boolean;
    plumbing_repairs: boolean;
    gas_safe: boolean;
    electrical_work: boolean;
    heating_cooling: boolean;
    carpentry: boolean;
  };
  emergency_contact: string;
  business_number: string;
  insurance_expiry: string;
  notifications: {
    email_alerts: boolean;
    sms_alerts: boolean;
    job_updates: boolean;
    marketing_emails: boolean;
  };
}

const ProviderProfileSettingsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const hasCheckedAuth = useRef(false);

  const [formData, setFormData] = useState<ProfileFormData>({
    business_name: '',
    business_type: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    business_description: '',
    service_areas: '',
    opening_time: '08:00',
    closing_time: '20:00',
    availability: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    },
    capabilities: {
      planning_flooring: true,
      plumbing_repairs: true,
      gas_safe: true,
      electrical_work: false,
      heating_cooling: false,
      carpentry: false,
    },
    emergency_contact: '',
    business_number: '',
    insurance_expiry: '',
    notifications: {
      email_alerts: true,
      sms_alerts: true,
      job_updates: true,
      marketing_emails: false,
    },
  });

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchProfileData();
    }
  }, [user?.id, authLoading]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/profile`);
      if (response.data?.profile) {
        setFormData((prev) => ({
          ...prev,
          ...response.data.profile,
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setHasChanges(true);
  };

  const handleSwitchChange = (key: string, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  const handleAvailabilityChange = (day: keyof typeof formData.availability) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: !prev.availability[day],
      },
    }));
    setHasChanges(true);
  };

  const handleCapabilityChange = (capability: keyof typeof formData.capabilities) => {
    setFormData((prev) => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [capability]: !prev.capabilities[capability],
      },
    }));
    setHasChanges(true);
  };

  const handleNotificationChange = (key: keyof typeof formData.notifications) => {
    setFormData((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
    setHasChanges(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiService.put(`/provider/profile`, formData);
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Profile settings updated successfully',
        });
        setHasChanges(false);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update profile settings',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to update profile settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Profile & Settings - VireWire Provider</title>
        <meta name="description" content="Manage your business profile and settings" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Profile & Settings</h1>
            <p className="text-gray-600 text-sm mt-2">Manage your business credentials, service capabilities, and operational preferences to ensure high-quality service delivery.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-8">
          {/* Main Content - 2/3 width */}
          <div className="col-span-2 space-y-6">
            {/* Business Profile Section */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="bg-gray-50 border-b border-gray-200">
                <CardTitle className="text-lg font-bold text-gray-900">Business Profile</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Business Name & Contact */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase font-semibold text-gray-700 tracking-wide">Business Name</Label>
                    <Input
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      placeholder="Premium Home Solutions Ltd"
                      className="mt-3 bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase font-semibold text-gray-700 tracking-wide">Contact Person</Label>
                    <Input
                      name="contact_person"
                      value={formData.business_type}
                      onChange={handleInputChange}
                      placeholder="Alexander Sterling"
                      className="mt-3 bg-gray-50 border-gray-200"
                    />
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase font-semibold text-gray-700 tracking-wide">Email Address</Label>
                      <Button type="button" variant="link" size="sm" className="text-accent p-0 h-auto text-xs">CHANGE</Button>
                    </div>
                    <Input
                      type="email"
                      value="a.sterling@premiumhome.co.uk"
                      readOnly
                      className="mt-0 bg-gray-100 border-gray-200 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase font-semibold text-gray-700 tracking-wide">Phone Number</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+44 7700 900123"
                      className="mt-3 bg-gray-50 border-gray-200"
                    />
                  </div>
                </div>

                {/* Company Reg & VAT */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase font-semibold text-gray-700 tracking-wide">Company Reg No</Label>
                    <Input
                      name="business_number"
                      value={formData.business_number}
                      onChange={handleInputChange}
                      placeholder="UK-8829441"
                      className="mt-3 bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase font-semibold text-gray-700 tracking-wide">VAT Number</Label>
                    <Input
                      name="vat_number"
                      value={formData.business_type || ""}
                      onChange={handleInputChange}
                      placeholder="GB 123 4567 89"
                      className="mt-3 bg-gray-50 border-gray-200"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Areas */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="bg-gray-50 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-gray-900">
                  📍 Service Areas
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div>
                  <p className="text-sm text-gray-600 mb-4">Your business is currently visible to clients in the following postcodes:</p>
                  <div className="flex flex-wrap gap-3">
                    {['SWIA', 'W1K', 'ECTV'].map((code) => (
                      <div key={code} className="bg-teal-50 border border-teal-300 rounded-full px-4 py-2 flex items-center gap-2">
                        <span className="font-semibold text-teal-700">{code}</span>
                        <button type="button" className="text-teal-600 hover:text-teal-700 font-bold">×</button>
                      </div>
                    ))}
                    <button 
                      type="button"
                      className="border-2 border-dashed border-gray-300 rounded-full px-4 py-2 text-accent hover:border-accent transition-colors font-semibold"
                    >
                      + ADD POSTCODE
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 h-48 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 font-semibold">Service Area Map</p>
                    <p className="text-sm text-gray-500">Showing coverage in {['SWIA', 'W1K', 'ECTV'].length} areas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Working Hours & Availability */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="bg-gray-50 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-gray-900">
                  ⏰ Working Hours & Availability
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Monday</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={formData.availability.monday}
                        onCheckedChange={() => handleAvailabilityChange('monday')}
                      />
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          name="opening_time"
                          value={formData.opening_time}
                          onChange={handleInputChange}
                          className="w-24"
                        />
                        <span>to</span>
                        <Input
                          type="time"
                          name="closing_time"
                          value={formData.closing_time}
                          onChange={handleInputChange}
                          className="w-24"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div>
                      <p className="text-sm font-semibold text-gray-400">Sunday</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={formData.availability.sunday}
                        onCheckedChange={() => handleAvailabilityChange('sunday')}
                      />
                      <p className="text-sm text-gray-500">Closed</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Capabilities */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="bg-gray-50 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-gray-900">
                  ⚙️ Service Capabilities
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                {[
                  { key: 'plumbing_repairs', label: 'Plumbing & Heating', level: 'Experienced' },
                  { key: 'electrical_work', label: 'Electrical Repairs', level: 'Expert (10+ Years)' }
                ].map(({ key, label, level }) => (
                  <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.capabilities[key as keyof typeof formData.capabilities]}
                        onCheckedChange={() =>
                          handleCapabilityChange(key as keyof typeof formData.capabilities)
                        }
                      />
                      <div>
                        <p className="font-semibold text-gray-900">{label}</p>
                        <p className="text-sm text-gray-600">{level}</p>
                      </div>
                    </div>
                    {formData.capabilities[key as keyof typeof formData.capabilities] && (
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                    )}
                  </div>
                ))}
                <button type="button" className="w-full mt-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-accent hover:text-accent transition-colors font-semibold">
                  + ADD CAPABILITY
                </button>
              </CardContent>
            </Card>

            {/* Operational Details & Notification Preferences in grid */}
            <div className="grid grid-cols-2 gap-6">
              {/* Operational Details */}
              <Card className="bg-white border border-gray-200">
                <CardHeader className="bg-gray-50 border-b border-gray-200">
                  <CardTitle className="flex items-center gap-2 text-lg font-bold text-gray-900">
                    🚐 Operational Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label className="text-xs uppercase font-semibold text-gray-700">Vehicle Type</Label>
                    <Input
                      name="vehicle_type"
                      value={formData.business_name || "Large Transit Van"}
                      onChange={handleInputChange}
                      className="mt-2 bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase font-semibold text-gray-700">Team Size</Label>
                    <Input
                      name="team_size"
                      value={formData.business_number || "2"}
                      onChange={handleInputChange}
                      className="mt-2 bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div className="space-y-3">
                    <Label className="text-xs uppercase font-semibold text-gray-700">Preferred Job Types</Label>
                    {['Emergency Callouts', 'Scheduled Maintenance'].map((type) => (
                      <div key={type} className="flex items-center gap-3 p-2">
                        <Checkbox defaultChecked />
                        <span className="text-sm text-gray-700">{type}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

            </div>

            {/* Save Button Section */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <Button
                type="submit"
                disabled={!hasChanges || loading}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </Button>
              {hasChanges && (
                <p className="text-sm text-orange-600 flex items-center font-medium my-auto">● Unsaved changes</p>
              )}
            </div>
          </div>

          {/* Right Sidebar - Provider Status */}
          <div className="col-span-1">
            <Card className="bg-white border border-gray-200 sticky top-[100px]">
              <CardContent className="p-6 space-y-6">
                {/* Provider ID */}
                <div className="border-b border-gray-200 pb-6">
                  <p className="text-xs uppercase font-semibold text-gray-600 tracking-wide mb-2">Provider ID</p>
                  <p className="text-2xl font-bold text-accent">{formData?.provider_id ? `#${formData.provider_id}` : '—'}</p>
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="w-3 h-3 bg-teal-600 rounded-full"></div>
                  <div>
                    <p className="font-semibold text-teal-900">{formData?.status || 'Pending'}</p>
                    <p className="text-xs text-teal-700">{formData?.verified ? 'Verified & eligible' : 'Verification pending'}</p>
                  </div>
                </div>

                {/* Account Info */}
                <div className="space-y-4 border-t border-gray-200 pt-6">
                  {formData?.member_since && (
                    <div>
                      <p className="text-xs uppercase font-semibold text-gray-600 tracking-wide mb-2">Member Since</p>
                      <p className="text-lg font-bold text-gray-900">{formData.member_since}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase font-semibold text-gray-600 tracking-wide mb-2">Total Jobs</p>
                    <p className="text-lg font-bold text-gray-900">{formData?.total_jobs ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase font-semibold text-gray-600 tracking-wide mb-2">Completion Rate</p>
                    <p className="text-lg font-bold text-teal-600">{formData?.completion_rate ? `${formData.completion_rate}%` : '—'}</p>
                  </div>
                </div>

                {/* Account Actions */}
                <div className="border-t border-gray-200 pt-6 space-y-3">
                  <h4 className="font-bold text-gray-900 text-sm">Account Actions</h4>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left"
                    onClick={async () => {
                      const newPassword = prompt('Enter new password (min 8 chars):');
                      if (newPassword && newPassword.length >= 8) {
                        try {
                          const res = await apiService.request('/account/change-password', 'POST', { new_password: newPassword });
                          if (res.success) {
                            toast({ title: 'Password Changed', description: 'Your password has been updated.' });
                          } else {
                            toast({ title: 'Error', description: res.message || 'Failed to change password', variant: 'destructive' });
                          }
                        } catch (e: any) {
                          toast({ title: 'Error', description: e.message || 'Failed to change password', variant: 'destructive' });
                        }
                      } else if (newPassword) {
                        toast({ title: 'Error', description: 'Password must be at least 8 characters', variant: 'destructive' });
                      }
                    }}
                  >
                    → Change Password
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left"
                    onClick={() => {
                      const section = document.querySelector('[data-section="notifications"]') || document.querySelector('h2');
                      if (section) section.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    → Notification Settings
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left"
                    onClick={() => toast({ title: 'Coming Soon', description: 'Bank details management will be available shortly.' })}
                  >
                    → Update Bank Details
                  </Button>
                </div>

                {/* Payout Details */}
                <div className="border-t border-gray-200 pt-6 bg-gray-50 -mx-6 -mb-6 px-6 py-6 rounded-b-lg">
                  <h4 className="font-bold text-gray-900 text-sm mb-4">💳 Payout Details</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Bank Account</p>
                      <p className="font-mono text-sm font-semibold text-gray-900">
                        {formData?.bank_account_last4 ? `•••• •••• •••• ${formData.bank_account_last4}` : 'Not set up'}
                      </p>
                      {formData?.bank_name && <p className="text-xs text-gray-600">{formData.bank_name}</p>}
                    </div>
                    <Button type="button" className="w-full bg-teal-600 hover:bg-teal-700 text-white mt-4">
                      {formData?.bank_account_last4 ? 'Update Payment Details' : 'Add Payment Details'}
                    </Button>
                  </div>
                </div>

                {/* Compliance Status */}
                {(formData?.documents_verified !== undefined || formData?.documents_total) && (
                  <div className="pt-4">
                    <h4 className="font-bold text-gray-900 text-sm mb-3">Compliance Status</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-700">Documents Verified</p>
                        <p className="font-bold text-gray-900">{formData?.documents_verified ?? 0} of {formData?.documents_total ?? 0}</p>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${formData?.documents_total ? ((formData?.documents_verified ?? 0) / formData.documents_total * 100) : 0}%` }}></div>
                      </div>
                      {formData?.documents_total && (formData?.documents_verified ?? 0) < formData.documents_total && (
                        <p className="text-xs text-gray-600 mt-2">{formData.documents_total - (formData?.documents_verified ?? 0)} document(s) require your attention.</p>
                      )}
                      <Button type="button" variant="link" className="text-accent p-0 h-auto text-sm mt-2">
                        View Compliance Center →
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
    </ProviderLayout>
  );
};

export default ProviderProfileSettingsPage;
